import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import {
  getDashboard,
  getDrivers,
  getDriverStats,
  getDriverStoreOrders,
  getDriverStoreStats,
} from "../../api/deliveryCompanyService";
import { formatCurrency } from "../Admin/constants";

const COUPON_DISCOUNT_TYPE_LABELS = {
  1: "Percentage",
  2: "Fixed Amount",
  3: "Free Delivery",
};

function couponLabel(code, discountType) {
  if (!code) return "-";
  const typeLabel = COUPON_DISCOUNT_TYPE_LABELS[discountType];
  return typeLabel ? `${code} (${typeLabel})` : code;
}

export default function Drivers() {
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    getDashboard()
      .then(setDashboard)
      .catch(() => {});
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {dashboard?.companyName || "Drivers"}
      </Typography>

      <DriversTab />
    </Box>
  );
}

function DriversTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);

  useEffect(() => {
    setLoading(true);
    getDrivers({})
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load drivers. Is the API running?"))
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      { field: "vehicleType", headerName: "Vehicle", width: 130, valueGetter: (v) => v || "-" },
      { field: "licensePlate", headerName: "Plate", width: 110, valueGetter: (v) => v || "-" },
      {
        field: "isAvailable",
        headerName: "Available",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Available" : "Offline"}
            color={params.value ? "success" : "default"}
          />
        ),
      },
      { field: "rating", headerName: "Rating", width: 100 },
      { field: "activeDeliveryCount", headerName: "Active", width: 100 },
      { field: "completedDeliveryCount", headerName: "Completed", width: 110 },
    ],
    []
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedDriver(params.row)}
          sx={{ border: "none", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      {selectedDriver && <DriverStatsPanel driver={selectedDriver} />}
    </Box>
  );
}

const DRIVER_STATS_PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "currentMonth", label: "Current Month" },
  { value: "pastMonth", label: "Past Month" },
  { value: "custom", label: "Custom" },
];

function DriverStatsPanel({ driver }) {
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [storeRows, setStoreRows] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState("");
  const [ordersModalStore, setOrdersModalStore] = useState(null);

  const rangeReady = period !== "custom" || (customStart && customEnd);
  const queryParams = useMemo(
    () => ({
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
    }),
    [period, customStart, customEnd]
  );

  useEffect(() => {
    if (!rangeReady) return;

    setLoading(true);
    getDriverStats(driver.userId, queryParams)
      .then((data) => {
        setStats(data);
        setError("");
      })
      .catch(() => setError("Could not load driver stats. Is the API running?"))
      .finally(() => setLoading(false));
  }, [driver.userId, queryParams, rangeReady]);

  useEffect(() => {
    if (!rangeReady) return;

    setStoreLoading(true);
    getDriverStoreStats(driver.userId, queryParams)
      .then((data) => {
        setStoreRows(data);
        setStoreError("");
      })
      .catch(() => setStoreError("Could not load store breakdown. Is the API running?"))
      .finally(() => setStoreLoading(false));
  }, [driver.userId, queryParams, rangeReady]);

  const storeColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 160, sortable: false },
      { field: "ownerName", headerName: "Business Owner", flex: 1, minWidth: 160, sortable: false },
      { field: "orderCount", headerName: "Orders", width: 90, sortable: false },
      { field: "productRevenue", headerName: "Products Revenue", width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeCommission", headerName: "Platform Fee (Store)", width: 160, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "discountAmount",
        headerName: "Coupon Discount",
        width: 150,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "freeDeliveryOrderCount",
        headerName: "Free Delivery Orders",
        width: 160,
        sortable: false,
      },
      { field: "storeNetRevenue", headerName: "Net To Store", width: 140, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryRevenue", headerName: "Delivery Revenue", width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryCommission", headerName: "Platform Fee (Delivery)", width: 170, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryNetRevenue", headerName: "Net From Delivery", width: 150, sortable: false, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.row.isTotalRow ? null : (
            <Button size="small" onClick={() => setOrdersModalStore(params.row)}>
              View Orders
            </Button>
          ),
      },
    ],
    []
  );

  const storeRowsWithTotal = useMemo(() => {
    if (storeRows.length === 0) return [];

    const totals = storeRows.reduce(
      (acc, s) => ({
        orderCount: acc.orderCount + (s.orderCount || 0),
        productRevenue: acc.productRevenue + (s.productRevenue || 0),
        storeCommission: acc.storeCommission + (s.storeCommission || 0),
        discountAmount: acc.discountAmount + (s.discountAmount || 0),
        freeDeliveryOrderCount: acc.freeDeliveryOrderCount + (s.freeDeliveryOrderCount || 0),
        storeNetRevenue: acc.storeNetRevenue + (s.storeNetRevenue || 0),
        deliveryRevenue: acc.deliveryRevenue + (s.deliveryRevenue || 0),
        deliveryCommission: acc.deliveryCommission + (s.deliveryCommission || 0),
        deliveryNetRevenue: acc.deliveryNetRevenue + (s.deliveryNetRevenue || 0),
      }),
      {
        orderCount: 0,
        productRevenue: 0,
        storeCommission: 0,
        discountAmount: 0,
        freeDeliveryOrderCount: 0,
        storeNetRevenue: 0,
        deliveryRevenue: 0,
        deliveryCommission: 0,
        deliveryNetRevenue: 0,
      }
    );

    return [
      ...storeRows,
      {
        storeId: "__total__",
        storeName: "Total",
        ownerName: "",
        isTotalRow: true,
        ...totals,
      },
    ];
  }, [storeRows]);

  return (
    <Box sx={{ mt: 3, p: 2, bgcolor: "background.paper", borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {driver.fullName}
      </Typography>

      <ToggleButtonGroup
        value={period}
        exclusive
        onChange={(e, value) => value && setPeriod(value)}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        {DRIVER_STATS_PERIODS.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {period === "custom" && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            type="date"
            label="From"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
          />
          <TextField
            type="date"
            label="To"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
          />
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!rangeReady ? (
        <Typography variant="body2" color="text.secondary">
          Choose a start and end date.
        </Typography>
      ) : (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ flexWrap: "wrap" }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Collected
              </Typography>
              <Typography variant="h4">
                {loading
                  ? "-"
                  : formatCurrency((stats?.storeCollectionsTotal ?? 0) + (stats?.deliveryRevenueTotal ?? 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Products + delivery across {stats?.orderCount ?? 0} orders
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Collected From Stores
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.storeCollectionsTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Product price total from {stats?.orderCount ?? 0} orders
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Revenue From Orders
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.deliveryRevenueTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total delivery price across {stats?.orderCount ?? 0} orders
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Platform Commission From Stores
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.storeCommissionTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Taken from product totals
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Platform Commission From Delivery
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.deliveryCommissionTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Taken from delivery fees
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Coupon Discounts Given
              </Typography>
              <Typography variant="h4">
                {loading ? "-" : formatCurrency(stats?.discountTotal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Absorbed by stores (incl. free-delivery coupons)
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Net For Delivery
              </Typography>
              <Typography variant="h4">
                {loading
                  ? "-"
                  : formatCurrency((stats?.deliveryRevenueTotal ?? 0) - (stats?.deliveryCommissionTotal ?? 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Delivery revenue minus our commission
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      )}

      {rangeReady && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            By Store
          </Typography>
          {storeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {storeError}
            </Alert>
          )}
          <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
            <DataGrid
              autoHeight
              rows={storeRowsWithTotal}
              getRowId={(row) => row.storeId}
              columns={storeColumns}
              loading={storeLoading}
              disableRowSelectionOnClick
              onRowClick={(params) => {
                if (!params.row.isTotalRow) setOrdersModalStore(params.row);
              }}
              getRowClassName={(params) => (params.row.isTotalRow ? "total-row" : "")}
              sx={{
                border: "none",
                "& .MuiDataGrid-row": { cursor: "pointer" },
                "& .total-row": {
                  bgcolor: "action.hover",
                  fontWeight: 700,
                  cursor: "default",
                  "& .MuiDataGrid-cell": { fontWeight: 700 },
                },
              }}
            />
          </Box>
        </Box>
      )}

      <DriverStoreOrdersDialog
        driverId={driver.userId}
        store={ordersModalStore}
        queryParams={queryParams}
        onClose={() => setOrdersModalStore(null)}
      />
    </Box>
  );
}

function DriverStoreOrdersDialog({ driverId, store, queryParams, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!store) return;

    setLoading(true);
    getDriverStoreOrders(driverId, store.storeId, queryParams)
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load orders. Is the API running?"))
      .finally(() => setLoading(false));
  }, [driverId, store, queryParams]);

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Order #", width: 130 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 140 },
      {
        field: "deliveredAt",
        headerName: "Delivered At",
        width: 170,
        valueFormatter: (v) => (v ? new Date(v).toLocaleString() : "-"),
      },
      { field: "productRevenue", headerName: "Products", width: 110, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeCommission", headerName: "Store Fee", width: 110, valueFormatter: (v) => formatCurrency(v) },
      {
        field: "couponCode",
        headerName: "Coupon",
        width: 170,
        renderCell: (params) =>
          params.row.couponCode ? (
            <Chip
              size="small"
              label={couponLabel(params.row.couponCode, params.row.couponDiscountType)}
              color={params.row.couponDiscountType === 3 ? "info" : "default"}
            />
          ) : (
            "-"
          ),
      },
      { field: "discountAmount", headerName: "Coupon Discount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "storeNetRevenue", headerName: "Net To Store", width: 130, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryRevenue", headerName: "Delivery", width: 110, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryCommission", headerName: "Delivery Fee", width: 120, valueFormatter: (v) => formatCurrency(v) },
      { field: "deliveryNetRevenue", headerName: "Net Delivery", width: 130, valueFormatter: (v) => formatCurrency(v) },
    ],
    []
  );

  return (
    <Dialog open={Boolean(store)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Orders - {store?.storeName}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.orderId}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </DialogContent>
    </Dialog>
  );
}

